/**
 * Browser push (FCM) for the web-app — PRODUCTION only.
 *
 * Registers the env-aware messaging service worker, obtains an FCM token, and
 * registers it with the backend (`registerDevice`, platform WEB) so the PUSH
 * channel can deliver OS notifications while the tab is backgrounded/closed
 * (shown by public/firebase-messaging-sw.js). Foreground/in-app alerts continue
 * to come through Socket.IO, so we deliberately don't double-handle them here.
 *
 * Everything Firebase is dynamically imported and gated on `import.meta.env.PROD`
 * + a present config, so dev and the initial bundle are untouched.
 */
import type { ApolloClient } from '@apollo/client';
import { REGISTER_DEVICE, UNREGISTER_DEVICE } from '@/graphql/notifications';
import { firebaseConfig, getFirebaseApp } from '@/lib/firebase';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;
const DEVICE_ID_KEY = 'promostork.web.deviceId';
const LAST_TOKEN_KEY = 'promostork.web.fcmToken';

/** Stable per-browser id so one browser keeps one DeviceToken row across rotations. */
function getOrCreateDeviceId(): string {
  let id = window.localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `web-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
    window.localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/** Config the service worker needs (messaging fields only), as query params. */
function swParams(): string {
  return new URLSearchParams({
    apiKey: firebaseConfig.apiKey,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    storageBucket: firebaseConfig.storageBucket,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
  }).toString();
}

let inFlight = false;

/**
 * Prompt for permission (only if not already decided), mint an FCM token, and
 * register it with the backend. No-op in dev, when unconfigured/unsupported, or
 * when permission is denied. Safe to call on every login — it self-dedupes.
 */
export async function registerWebPush(client: ApolloClient<object>): Promise<void> {
  if (!import.meta.env.PROD || inFlight) return;
  if (!VAPID_KEY || !firebaseConfig.apiKey) return;
  if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
    return;
  }
  if (Notification.permission === 'denied') return;

  inFlight = true;
  try {
    const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
    if (!(await isSupported())) return;

    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      if (result !== 'granted') return;
    }

    const app = await getFirebaseApp();
    if (!app) return;

    const serviceWorkerRegistration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?${swParams()}`,
    );
    const fcmToken = await getToken(getMessaging(app), {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration,
    });
    if (!fcmToken) return;

    // Skip the network round-trip if this exact token is already registered.
    if (window.localStorage.getItem(LAST_TOKEN_KEY) === fcmToken) return;

    await client.mutate({
      mutation: REGISTER_DEVICE,
      variables: {
        input: {
          token: fcmToken,
          platform: 'WEB',
          deviceId: getOrCreateDeviceId(),
          appVersion: 'web',
          osVersion: navigator.userAgent,
          deviceModel: 'browser',
        },
      },
    });
    window.localStorage.setItem(LAST_TOKEN_KEY, fcmToken);
  } catch {
    // Best-effort — never surface push errors to the user.
  } finally {
    inFlight = false;
  }
}

/** Best-effort: revoke the FCM token and unregister it server-side on logout. */
export async function unregisterWebPush(client: ApolloClient<object>): Promise<void> {
  if (typeof window === 'undefined') return;
  const last = window.localStorage.getItem(LAST_TOKEN_KEY);
  window.localStorage.removeItem(LAST_TOKEN_KEY);
  if (!last) return;
  try {
    const app = await getFirebaseApp();
    if (app) {
      const { getMessaging, deleteToken } = await import('firebase/messaging');
      await deleteToken(getMessaging(app));
    }
  } catch {
    // ignore
  }
  try {
    await client.mutate({ mutation: UNREGISTER_DEVICE, variables: { token: last } });
  } catch {
    // ignore
  }
}
